/**
 * Session and the whitelist gate.
 *
 * This check is UX, not security. The real gate is that every table in panelgear-internal
 * grants `team:internal` and nothing else, so a signed-in non-member reads nothing even if
 * they get past this. See docs/technical/internal-tools-plan.md §2.4.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Models } from 'appwrite';
import { account, teams, OAuthProvider, INTERNAL_TEAM_ID } from 'src/api/appwrite';

export type AuthStatus = 'loading' | 'signed-out' | 'not-authorised' | 'ready';

interface AuthContextValue {
  status: AuthStatus;
  user: Models.User<Models.Preferences> | null;
  signIn: (provider: 'google' | 'microsoft') => void;
  signOut: () => Promise<void>;
  refresh: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<Models.User<Models.Preferences> | null>(null);
  const [token, setToken] = useState(0);

  const refresh = useCallback(() => setToken((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const current = await account.get();
        if (cancelled) return;

        const { teams: memberships } = await teams.list();
        if (cancelled) return;

        const isInternal = memberships.some((team) => team.$id === INTERNAL_TEAM_ID);
        if (!isInternal) {
          // Sign them out rather than leaving a session that can reach nothing. Leaving it
          // in place produces an app that renders empty screens with no explanation.
          await account.deleteSession('current').catch(() => undefined);
          if (cancelled) return;
          setUser(null);
          setStatus('not-authorised');
          return;
        }

        setUser(current);
        setStatus('ready');
      } catch {
        if (cancelled) return;
        setUser(null);
        setStatus('signed-out');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const signIn = useCallback((provider: 'google' | 'microsoft') => {
    // Redirects the browser; nothing after this runs.
    account.createOAuth2Session({
      provider: provider === 'microsoft' ? OAuthProvider.Microsoft : OAuthProvider.Google,
      success: `${window.location.origin}/`,
      failure: `${window.location.origin}/login?error=sign-in-failed`,
    });
  }, []);

  const signOut = useCallback(async () => {
    await account.deleteSession('current').catch(() => undefined);
    setUser(null);
    setStatus('signed-out');
  }, []);

  const value = useMemo(
    () => ({ status, user, signIn, signOut, refresh }),
    [status, user, signIn, signOut, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
